import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Crosshair, RefreshCw, Loader2 } from 'lucide-react';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useZoneSystem, usePerformancePaceZones } from '@/hooks/useClosedLoopAnalytics';
import { syncStravaData } from '@/lib/api';
import { calibratePaceZones } from '@/lib/analyticsApi';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';

// Polarized distribution targets (Seiler 2010): 75% easy / 7% moderate / 18% hard
const POLARIZED_TARGET_PCT: Record<number, number> = { 1: 30, 2: 45, 3: 7, 4: 12, 5: 6 };

const ZONE_COLORS: Record<number, string> = {
  1: 'hsl(170, 80%, 45%)',
  2: 'hsl(160, 70%, 50%)',
  3: 'hsl(50, 80%, 55%)',
  4: 'hsl(35, 85%, 55%)',
  5: 'hsl(15, 85%, 55%)',
};

function formatPaceBound(val: number): string {
  const mins = Math.floor(val);
  const secs = Math.round((val - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Convert m/s speed to min/km pace */
function speedToPace(speedMs: number): number {
  if (speedMs <= 0) return 99;
  return 1000 / speedMs / 60; // min/km
}

const SOURCE_LABELS: Record<string, string> = {
  configured: 'MANUALLY SET',
  inferred: 'ESTIMATED',
  vdot: 'VDOT',
};

export function SystemCalibrationCard() {
  const { t } = useTranslation();
  const [isRecalibrating, setIsRecalibrating] = useState(false);
  const queryClient = useQueryClient();
  const { formatPaceFromMinPerKm, formatPaceRange, paceUnit } = useUnitSystem();
  const { data: zoneSystem } = useZoneSystem('run', 'pace');
  const { data: perfZones } = usePerformancePaceZones();

  // Primary: zone system bands. Fallback: performance analytics pace_zone_definitions.
  const { zones, zoneSource, anchorLabel } = (() => {
    // Try zone system first
    if (zoneSystem?.bands && zoneSystem.bands.length > 0) {
      const built = zoneSystem.bands.map((band) => ({
        id: band.zone_number,
        label: `Z${band.zone_number}`,
        name: band.name.toUpperCase(),
        paceRange: `${formatPaceBound(band.lower_bound)}–${formatPaceBound(band.upper_bound)}`,
        pct: POLARIZED_TARGET_PCT[band.zone_number] ?? Math.round(100 / zoneSystem.bands.length),
        color: band.color ?? ZONE_COLORS[band.zone_number] ?? 'hsl(210, 30%, 50%)',
      }));
      const src = zoneSystem.method === 'vdot' ? 'VDOT' : zoneSystem.method === 'threshold' ? 'THRESHOLD' : null;
      const anchor = zoneSystem.method === 'vdot'
        ? `VDOT ${zoneSystem.anchor_value?.toFixed(1)}`
        : zoneSystem.anchor_value
          ? `LT2 ${formatPaceBound(zoneSystem.anchor_value)}`
          : null;
      return { zones: built, zoneSource: src, anchorLabel: anchor };
    }

    // Fallback: performance analytics pace_zone_definitions
    if (perfZones && perfZones.zones.length > 0) {
      const built = perfZones.zones.map((pz, idx) => {
        const zoneNum = idx + 1;
        const minPace = pz.min_speed_ms != null ? speedToPace(pz.min_speed_ms) : null;
        const maxPace = pz.max_speed_ms != null ? speedToPace(pz.max_speed_ms) : null;

        let paceRange: string;
        if (zoneNum === 1 && maxPace != null) {
          paceRange = `> ${formatPaceBound(maxPace)}`;
        } else if (zoneNum === perfZones.zones.length && minPace != null) {
          paceRange = `< ${formatPaceBound(minPace)}`;
        } else if (minPace != null && maxPace != null) {
          paceRange = `${formatPaceBound(minPace)}–${formatPaceBound(maxPace)}`;
        } else {
          paceRange = '—';
        }

        return {
          id: zoneNum,
          label: `Z${zoneNum}`,
          name: pz.name.toUpperCase(),
          paceRange,
          pct: POLARIZED_TARGET_PCT[zoneNum] ?? 20,
          color: ZONE_COLORS[zoneNum] ?? 'hsl(210, 30%, 50%)',
        };
      });

      const src = perfZones.threshold_pace_source
        ? SOURCE_LABELS[perfZones.threshold_pace_source] ?? perfZones.threshold_pace_source.toUpperCase()
        : 'PERFORMANCE';
      let anchor: string | null = null;
      if (perfZones.threshold_pace_ms != null && perfZones.threshold_pace_ms > 0) {
        const ltPace = speedToPace(perfZones.threshold_pace_ms);
        anchor = `LT2 ${formatPaceFromMinPerKm(ltPace)}`;
      }
      return { zones: built, zoneSource: src, anchorLabel: anchor };
    }

    return { zones: null, zoneSource: null, anchorLabel: null };
  })();

  const hasZones = zones !== null;

  const handleRecalibrate = async () => {
    setIsRecalibrating(true);
    try {
      // Step 1: Sync latest activities from Strava
      await syncStravaData();

      // Step 2: Explicitly trigger zone calibration
      await calibratePaceZones();

      // Step 3: Invalidate cached queries so UI refreshes
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['performancePaceZones'] });

      // Step 4: Allow DB to settle, then refetch zones
      await new Promise(r => setTimeout(r, 1000));
      try {
        await queryClient.refetchQueries({ queryKey: ['zones'] });
        await queryClient.refetchQueries({ queryKey: ['performancePaceZones'] });
      } catch (zoneErr) {
        // Zones may need a moment — don't block the success state
        console.warn('Zone refetch after calibration failed, will retry on next load', zoneErr);
      }

      toast({
        title: t('settings.calibration.recalibrationInitiated'),
        description: t('settings.calibration.recalibrationInitiatedDesc'),
      });
    } catch (error) {
      toast({
        title: t('settings.calibration.calibrationFailed'),
        description: error instanceof Error ? error.message : t('settings.calibration.calibrationFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsRecalibrating(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Crosshair className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">{t('settings.calibration.title')}</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
            {t('settings.calibration.subtitle')}
          </p>
        </div>
        <span className="ml-auto text-[8px] font-mono tracking-wider text-white/20 uppercase">
          {hasZones ? (zoneSource ? `${zoneSource}-DERIVED` : t('settings.calibration.readOnly')) : 'NOT CALIBRATED'}
        </span>
      </div>

      {/* Anchor badge */}
      {anchorLabel && (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-[#00FFC8]/60 px-2 py-0.5 rounded border border-[#00FFC8]/15 bg-[#00FFC8]/5">
            {anchorLabel}
          </span>
        </div>
      )}

      {/* Zone Grid */}
      {hasZones ? (
      <div className="space-y-1.5">
        {/* Grid Header */}
        <div className="grid grid-cols-[4rem_1fr_5rem_3rem] gap-2 px-2 mb-1">
          <span className="text-[8px] font-mono tracking-[0.15em] uppercase text-white/20">{t('settings.calibration.gridZone')}</span>
          <span className="text-[8px] font-mono tracking-[0.15em] uppercase text-white/20">{t('settings.calibration.gridPaceRange')}</span>
          <span className="text-[8px] font-mono tracking-[0.15em] uppercase text-white/20 text-right">{t('settings.calibration.gridShare')}</span>
          <span />
        </div>

        {zones.map((zone) => (
          <div
            key={zone.id}
            className="grid grid-cols-[4rem_1fr_5rem_3rem] gap-2 items-center px-2 py-2 rounded-md border border-white/[0.04] bg-white/[0.01]"
          >
            {/* Zone label */}
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: zone.color, boxShadow: `0 0 6px ${zone.color}50` }}
              />
              <span className="text-[10px] font-mono font-medium text-foreground">{zone.label}</span>
            </div>

            {/* Pace range */}
            <span className="text-[10px] font-mono tabular-nums text-[#A0A0A0]">
              {zone.paceRange.includes('>') || zone.paceRange.includes('<')
                ? zone.paceRange.replace(/[\d]+:[\d]+/g, (m) => `${formatPaceRange(m)}`)
                : `${formatPaceRange(zone.paceRange)}${paceUnit}`
              }
            </span>

            {/* Percentage */}
            <span className="text-[10px] font-mono tabular-nums text-foreground text-right">
              {zone.pct}%
            </span>

            {/* Mini bar */}
            <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${zone.pct}%`,
                  backgroundColor: zone.color,
                  boxShadow: `0 0 6px ${zone.color}40`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="py-6 text-center space-y-2">
          <p className="text-[10px] font-mono text-white/40 tracking-wider uppercase">
            No pace zones calibrated
          </p>
          <p className="text-[9px] font-mono text-white/25 max-w-[240px] mx-auto">
            Add race results in Training Preferences to calculate zones using Daniels VDOT methodology
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-white/[0.06] space-y-3">
        <p className="text-[9px] font-mono text-white/25 tracking-wider text-center uppercase">
          {t('settings.calibration.footer')}
        </p>

        {/* RE-SYNC & CALIBRATE Button */}
        <button
          onClick={handleRecalibrate}
          disabled={isRecalibrating}
          className={cn(
            'w-full flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-md font-mono transition-all duration-200 border',
            'border-[rgba(0,255,200,0.25)] bg-[rgba(0,255,200,0.06)] text-[#00FFC8]',
            'hover:bg-[rgba(0,255,200,0.12)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]',
            'disabled:opacity-50 disabled:pointer-events-none'
          )}
        >
          {isRecalibrating ? (
            <span className="flex items-center gap-2 text-[10px] tracking-[0.12em] uppercase">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('settings.calibration.recalibrating')}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-2 text-[10px] tracking-[0.12em] uppercase">
                <RefreshCw className="h-3.5 w-3.5" /> {t('settings.calibration.recalibrate')}
              </span>
              <span className="text-[8px] tracking-wider text-white/30 uppercase">
                {t('settings.calibration.recalibrateDesc')}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
